import {
  CSSProperties,
  forwardRef,
  HTMLAttributes,
  MouseEvent,
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { designSystemVars as vars } from "../tokens";

export type AnchoredOverlayAlign = "left" | "right";
export type AnchoredOverlayPlacement = "top" | "bottom";

interface OverlayPosition {
  left: number;
  top: number;
  anchorWidth?: number;
  availableHeight: number;
  placement: AnchoredOverlayPlacement;
  ready: boolean;
}

const OverlayRoot = styled.div`
  position: absolute;
  z-index: ${vars.zIndex.dropdown};
`;

export interface AnchoredOverlayProps extends HTMLAttributes<HTMLDivElement> {
  anchorRef?: RefObject<HTMLElement | null>;
  matchAnchorWidth?: boolean;
  align?: AnchoredOverlayAlign;
  gap?: number;
  viewportPadding?: number;
  preferredPlacement?: AnchoredOverlayPlacement;
}

const toCssLength = (value: CSSProperties["maxHeight"]) =>
  typeof value === "number" ? `${value}px` : value;

export const AnchoredOverlay = forwardRef<HTMLDivElement, AnchoredOverlayProps>(
  (
    {
      anchorRef,
      matchAnchorWidth = false,
      align = "right",
      gap = 0,
      viewportPadding = 8,
      preferredPlacement = "bottom",
      onMouseDown,
      style,
      ...props
    },
    forwardedRef,
  ) => {
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState<OverlayPosition>({
      left: 0,
      top: 0,
      availableHeight: 0,
      placement: preferredPlacement,
      ready: false,
    });

    const updatePosition = useCallback(() => {
      const anchor = anchorRef?.current;
      const overlay = overlayRef.current;
      if (!anchor || !overlay) return;

      const anchorRect = anchor.getBoundingClientRect();
      const anchorWidth = matchAnchorWidth ? anchorRect.width : undefined;
      const overlayWidth = anchorWidth ?? overlay.offsetWidth;
      const overlayHeight = overlay.offsetHeight;
      const spaceBelow = Math.max(
        0,
        window.innerHeight - anchorRect.bottom - gap - viewportPadding,
      );
      const spaceAbove = Math.max(0, anchorRect.top - gap - viewportPadding);
      const shouldFlip =
        preferredPlacement === "bottom"
          ? overlayHeight > spaceBelow && spaceAbove > spaceBelow
          : overlayHeight > spaceAbove && spaceBelow > spaceAbove;
      const placement = shouldFlip
        ? preferredPlacement === "bottom"
          ? "top"
          : "bottom"
        : preferredPlacement;
      const availableHeight = placement === "bottom" ? spaceBelow : spaceAbove;
      const desiredLeft =
        align === "right" ? anchorRect.right - overlayWidth : anchorRect.left;
      const maxLeft = Math.max(
        viewportPadding,
        window.innerWidth - overlayWidth - viewportPadding,
      );
      const left = Math.min(Math.max(desiredLeft, viewportPadding), maxLeft);
      const renderedHeight = Math.min(overlayHeight, availableHeight);
      const top =
        placement === "bottom"
          ? anchorRect.bottom + gap
          : Math.max(viewportPadding, anchorRect.top - gap - renderedHeight);

      setPosition({
        left,
        top,
        anchorWidth,
        availableHeight,
        placement,
        ready: true,
      });
    }, [
      align,
      anchorRef,
      gap,
      matchAnchorWidth,
      preferredPlacement,
      viewportPadding,
    ]);

    useLayoutEffect(() => {
      if (!anchorRef?.current) return;

      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      const resizeObserver =
        typeof ResizeObserver === "undefined"
          ? null
          : new ResizeObserver(updatePosition);
      if (resizeObserver) {
        resizeObserver.observe(anchorRef.current);
        if (overlayRef.current) resizeObserver.observe(overlayRef.current);
      }

      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
        resizeObserver?.disconnect();
      };
    }, [anchorRef, updatePosition]);

    const setRef = (element: HTMLDivElement | null) => {
      overlayRef.current = element;
      if (typeof forwardedRef === "function") forwardedRef(element);
      else if (forwardedRef) forwardedRef.current = element;
    };

    const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
      onMouseDown?.(event);
      event.stopPropagation();
    };

    const overlay = (
      <OverlayRoot
        {...props}
        ref={setRef}
        data-placement={position.placement}
        onMouseDown={handleMouseDown}
        style={{
          ...style,
          width: position.anchorWidth ?? style?.width,
          minWidth: position.anchorWidth ?? style?.minWidth,
          position: anchorRef ? "fixed" : style?.position,
          top: anchorRef ? position.top : style?.top,
          right: anchorRef ? "auto" : style?.right,
          bottom: anchorRef ? "auto" : style?.bottom,
          left: anchorRef ? position.left : style?.left,
          maxHeight:
            anchorRef && position.ready
              ? style?.maxHeight
                ? `min(${toCssLength(style.maxHeight)}, ${position.availableHeight}px)`
                : position.availableHeight
              : style?.maxHeight,
          visibility:
            anchorRef && !position.ready ? "hidden" : style?.visibility,
        }}
      />
    );

    if (!anchorRef || typeof document === "undefined") return overlay;
    return createPortal(overlay, document.body);
  },
);

AnchoredOverlay.displayName = "AnchoredOverlay";
